/**
 * 처방 판정 테스트 패널 — 등급 라벨·톤·미판정 사유 문구(설계 결정 10).
 *
 * 뜻으로 부른다(세기 아님): critical→위험, caution→1회 과량, warning→1회 부족,
 * info→판단 불가, null→적정. 5값 밖 severity·맵에 없는 사유는 폴백으로 처리해
 * 빈 문구·빈 배지를 내지 않는다(제품식별 정본화 spec 머지 순서와 무관하게 버틴다).
 *
 * ⚠️ 라벨·톤은 status 우선이다(@spec:2026-09-18-처방검토-미달경고-참고등급 D-5):
 *    status가 "underdose"면 severity가 warning(옛 응답)이든 info(새 응답)이든
 *    "1회 부족(참고)" · 참고 톤(과량 카드와 다른 톤)을 낸다. 그 밖의 info는 "판단 불가"
 *    그대로. 미달 판정만 severity가 warning→info로 낮아져도 화면 라벨이 뒤바뀌지 않게
 *    한다(옛·새 백엔드 응답 공존). status가 없으면 순수 등급 라벨(불변)로 폴백한다.
 *
 * ⚠️ API 문서(`dose-review-api.md`)에 화면 문구가 확정된 사유(code_missing·
 *    insurance_code_not_registered·insurance_code_ambiguous)는 그 문구를 글자 그대로 쓴다.
 *    나머지는 "한 줄 뜻" 초안이며 지니님이 써 보며 고친다.
 */
import type { ColorSystem } from "components/design/design-system";
import type { Severity, DoseStatusStr, DoseReviewData } from "api/doseReviewService";

/**
 * 등급 라벨(AC-38). status가 "underdose"면 등급과 무관하게 "1회 부족(참고)"(D-5).
 * status 없으면 순수 등급 라벨. 5값 밖은 원문 문자열(AC-38b).
 */
export const severityLabel = (s: Severity | string, status?: DoseStatusStr): string => {
  if (status === "underdose") return "1회 부족(참고)";
  switch (s) {
    case "critical":
      return "위험";
    case "caution":
      return "1회 과량";
    case "warning":
      return "1회 부족";
    case "info":
      return "판단 불가";
    case null:
      return "적정";
    default:
      return String(s);
  }
};

/**
 * 배지 톤(AC-38c). status가 "underdose"면 참고 톤(회색) — 과량(caution/warning=Yellow)과
 * 다른 톤이어야 한다(D-5). status 없으면 순수 등급 톤. 5값 밖은 회색(AC-38b).
 */
export const severityTone = (s: Severity | string, status?: DoseStatusStr): ColorSystem => {
  if (status === "underdose") return "Gray";
  switch (s) {
    case "critical":
      return "Red";
    case "caution":
    case "warning":
      return "Yellow";
    case "info":
      return "Gray";
    case null:
      return "Blue";
    default:
      return "Gray";
  }
};

/** 등급 순위(카드 대표 등급·정렬용). critical 4 > caution 3 > warning 2 > info 1 > null 0. */
export const severityRank = (s: Severity | string): number => {
  switch (s) {
    case "critical":
      return 4;
    case "caution":
      return 3;
    case "warning":
      return 2;
    case "info":
      return 1;
    case null:
      return 0;
    default:
      return 0;
  }
};

/**
 * 미판정 사유 문구 맵 — 구현 시점(2026-09-17) 백엔드가 실제 배출하는 11종.
 * 앞 3종은 API 문서 `dose-review-api.md:129` 확정 문구(글자 단위 일치, AC-43).
 * 나머지 8종은 API 문서의 의미 설명·사유 갈래(`:127`)에 근거한 한 줄 뜻 초안.
 */
export const REASON_PHRASES: Record<string, string> = {
  // API 문서 확정 문구(verbatim)
  code_missing: "검토에 쓸 수 있는 약품 코드(표준코드·보험코드)가 없습니다",
  insurance_code_not_registered: "보험코드로 찾았으나 등록되지 않은 약입니다",
  insurance_code_ambiguous: "보험코드만으로는 약을 특정할 수 없습니다(표준코드 필요)",
  // 한 줄 뜻 초안(지니님 조정 대상)
  product_not_registered: "표준코드는 있으나 등록되지 않은 약입니다(보험코드 없음)",
  not_eligible: "용량 판정 대상이 아닌 약입니다",
  weight_required: "체중이 있어야 판정할 수 있는 약입니다(체중당 용량)",
  rule_not_found:
    "이 약에 맞는 용량 룰을 찾지 못했습니다. 차단·반려/보관 상태 룰은 판정에 들어가지 않습니다.",
  blocked: "검산에 걸려 판정에서 빠진 룰입니다",
  awaiting_unit: "처방 단위가 아직 확정되지 않은 약입니다(비급여 단위 미확정)",
  prescribe_unit_missing: "처방 단위 정보가 없어 판정할 수 없습니다",
  no_bridge: "코드에서 성분·제품으로 이어지는 연결 정보가 없습니다",
};

/** 사유 문구. 맵에 없으면 `사유 코드: {원문}`(AC-43b). null/빈값이면 빈 문자열. */
export const reasonPhrase = (reason: string | null | undefined): string => {
  if (!reason) return "";
  return REASON_PHRASES[reason] ?? `사유 코드: ${reason}`;
};

/**
 * 요약 줄(AC-44) — 서버 summary를 등급 라벨로 1:1. caution 뒤 `(낮춘 위험 M 포함)`은
 * M>0일 때만(AC-39b). M = 채택 판정에 pendingDowngrade가 있는 약품 수(cardModel.downgradedItemCount).
 *
 * "1회 부족(참고)" 수는 `summary.underdose`(백엔드 Phase 2 신규 필드)를 쓰고, 옛 응답이라
 * 없으면 `summary.warning`으로 폴백한다(D-5). "판단 불가"는 미달을 뺀 참고 수(`summary.info`).
 */
export const summaryLineText = (
  summary: DoseReviewData["summary"],
  downgradedCount: number
): string => {
  const cautionText =
    downgradedCount > 0
      ? `1회 과량 ${summary.caution} (낮춘 위험 ${downgradedCount} 포함)`
      : `1회 과량 ${summary.caution}`;
  return [
    `총 ${summary.total}`,
    `적정 ${summary.appropriate}`,
    `위험 ${summary.critical}`,
    cautionText,
    `1회 부족(참고) ${summary.underdose ?? summary.warning}`,
    `판단 불가 ${summary.info}`,
    `미판정 ${summary.unevaluated}`,
    `약국 룰 완화 ${summary.mitigated}`,
  ].join(" · ");
};
